import { useEffect, useState } from 'react';
import useModalState from '@sdk/components/ui/modal/useModalState';
import useAuth from '@sdk/authentication/useAuth';
import getActualClockInTimes, { ClockInTime } from '@sdk/timeClock/utils/getActualClockInTimes';
import useSlingApi from '@sdk/api/useSlingApi';
import useToast from '@sdk/common/toast/useToast';
import getErrorForToast from '@sdk/api/utils/getErrorForToast';
import { BreakCategory } from '@sdk/settings/types';
import { getEstimatedBreaksDuration } from '@sdk/timesheets/helpers/getEstimatedBreaksDuration';

import { useAutomatedBreaksSettings } from '@web/settings/components/schedule/Breaks/breaks.hooks';
import { UseEmployeePendingConsentTimesheetViewModalProps } from '@web/timesheets/EmployeePendingConsentTimesheetViewModal/employeePendingConsentTimesheetViewModal.types';
import useIsAutomatedBreaksFeatureFlagEnabled from '@web/automatedBreaks/hooks/useIsAutomatedBreaksFeatureFlagEnabled';

export const useEmployeePendingConsentTimesheetViewModal = ({
  isTimesheetPreviewActive,
  onClose,
  shift,
  onApprove,
  onDeny,
}: UseEmployeePendingConsentTimesheetViewModalProps) => {
  const { isVisible, open, close } = useModalState();
  const [isHistoryView, setIsHistoryView] = useState(false);
  const { currentUser } = useAuth();
  const { showErrorToast } = useToast();
  const api = useSlingApi();
  const automatedBreaksFeatureEnabled = useIsAutomatedBreaksFeatureFlagEnabled();
  const { automatedBreaks } = useAutomatedBreaksSettings();

  const modalConfig = { hideClose: true, noDismiss: true };

  useEffect(() => {
    // hook useses isTimesheetPreviewActive as parent (angular component) doesn't have open() method exposed.
    if (isTimesheetPreviewActive && !isVisible) {
      open();
    } else if (!isTimesheetPreviewActive && isVisible) {
      close();
    }
  }, [isTimesheetPreviewActive, isVisible, open]);

  const handleClose = () => {
    onClose();
    close();
  };

  const deny = async (reason: string) => {
    try {
      const { data } = await api.timesheets.denyConsent(
        reason,
        shift.timesheetProjections.map(({ id }) => ({ id }))
      );

      onDeny({ updatedTimesheetsStatuses: data, shiftId: shift.id });
    } catch (e) {
      showErrorToast(getErrorForToast(e));
      // throw error so progress button still show X
      throw e;
    }
  };

  const approve = async () => {
    try {
      const { data } = await api.timesheets.approveConsent(
        shift.timesheetProjections.map(({ id }) => ({ id }))
      );
      onApprove({ updatedTimesheetsStatuses: data, shiftId: shift.id });
    } catch (e) {
      showErrorToast(getErrorForToast(e));
      // throw error so progress button still show X
      throw e;
    }
  };

  const { usTimeFormat, worldClockEnabled } = currentUser.settings;
  const clockInTimes = getActualClockInTimes({
    automatedBreaksFeatureEnabled,
    automatedBreaksSettings: automatedBreaks,
    areStandardBreaksPaid: currentUser.orgSettings.shiftBreakType === BreakCategory.paid,
    worldClockEnabled,
    timezone: shift.location.timezone,
    timesheetEntries: shift.timesheetEntries,
    timesheetProjections: shift.timesheetProjections,
    scheduledBreaksDuration: getEstimatedBreaksDuration({
      shift,
      automatedBreaksSettings: automatedBreaks,
      orgSettings: currentUser.orgSettings,
    }),
  });

  let clockInTimesBefore: ClockInTime[] = null;
  if ('timesheetEntriesBefore' in shift) {
    if (shift.timesheetEntriesBefore.length === 0) {
      clockInTimesBefore = [];
    } else {
      clockInTimesBefore = getActualClockInTimes({
        automatedBreaksFeatureEnabled,
        automatedBreaksSettings: automatedBreaks,
        areStandardBreaksPaid: currentUser.orgSettings.shiftBreakType === BreakCategory.paid,
        worldClockEnabled,
        timezone: shift.location.timezone,
        timesheetEntries: shift.timesheetEntriesBefore,
        timesheetProjections: shift.timesheetProjections,
        scheduledBreaksDuration: getEstimatedBreaksDuration({
          shift,
          automatedBreaksSettings: automatedBreaks,
          orgSettings: currentUser.orgSettings,
        }),
      });
    }
  }

  const hasAnyTimesheet = Boolean(clockInTimes.length) || Boolean(clockInTimesBefore?.length);

  return {
    isVisible,
    modalConfig,
    automatedBreaks,
    usTimeFormat,
    clockInTimes,
    clockInTimesBefore,
    hasAnyTimesheet,
    handleClose,
    isHistoryView,
    setIsHistoryView,
    approve,
    deny,
  };
};
